// FP&A line-code catalogue — the canonical P&L lines a QBO account can map to.
// Mirrors the server taxonomy (services/fpaActuals CLASSIFICATION_LINE_FALLBACK
// + services/fpaVariance categorize()): a code is valid iff it categorises into
// one of the four families. cost.fixed.* / cost.var.* / rev.* allow open-ended
// sub-codes; the presets below cover the common ones, with a custom escape hatch.

export interface LineCodeOption { code: string; label: string }
export interface LineCodeGroup { group: string; options: LineCodeOption[] }

export const LINE_CODE_GROUPS: LineCodeGroup[] = [
  {
    group: 'Revenue',
    options: [
      { code: 'rev.tm', label: 'Time & Materials' },
      { code: 'rev.ff', label: 'Fixed-Fee' },
      { code: 'rev.sub', label: 'Subscription' },
      { code: 'rev', label: 'Revenue — uncategorized' },
    ],
  },
  {
    group: 'Labor',
    options: [{ code: 'cost.salary', label: 'Salaries & wages' }],
  },
  {
    group: 'Variable cost (COGS)',
    options: [
      { code: 'cost.var.labor', label: 'Billable / project labor' },
      { code: 'cost.var.materials', label: 'Materials & subcontractors' },
      { code: 'cost.var', label: 'Variable cost — uncategorized' },
    ],
  },
  {
    group: 'Fixed cost (operating)',
    options: [
      { code: 'cost.fixed.rent', label: 'Rent & facilities' },
      { code: 'cost.fixed.software', label: 'Software & tools' },
      { code: 'cost.fixed.marketing', label: 'Marketing' },
      { code: 'cost.fixed.admin', label: 'G&A / administrative' },
      { code: 'cost.fixed', label: 'Fixed cost — uncategorized' },
    ],
  },
];

const ALL_OPTIONS: LineCodeOption[] = LINE_CODE_GROUPS.flatMap((g) => g.options);
const LABELS = new Map(ALL_OPTIONS.map((o) => [o.code, o.label]));

/** True if `code` is one of the curated preset codes (vs. a custom sub-code). */
export function isPresetLineCode(code: string): boolean {
  return LABELS.has(code);
}

/** Human label for a code; falls back to the raw code for custom sub-codes. */
export function lineCodeLabel(code: string | null): string {
  if (!code) return 'Unmapped';
  return LABELS.get(code) ?? code;
}

/** QBO classification → coarse line code used when an account is unmapped.
 *  Mirrors services/fpaActuals.CLASSIFICATION_LINE_FALLBACK. */
export const CLASSIFICATION_FALLBACK: Record<string, string> = {
  Revenue: 'rev',
  Income: 'rev',
  CostOfGoodsSold: 'cost.var',
  Expense: 'cost.fixed',
};

/** Effective line code for an account: explicit map, else classification
 *  fallback, else null (unmapped, dropped into the unmapped bucket). */
export function effectiveLineCode(lineCode: string | null, classification: string | null): string | null {
  if (lineCode) return lineCode;
  if (classification && CLASSIFICATION_FALLBACK[classification]) return CLASSIFICATION_FALLBACK[classification];
  return null;
}

export type LineCodeFamily = 'revenue' | 'cost.salary' | 'cost.variable' | 'cost.fixed';

/** Coarse family a code rolls into (matches server categorize()); null if the
 *  code wouldn't categorise — i.e. it would silently vanish from variance. */
export function lineCodeFamily(code: string | null): LineCodeFamily | null {
  if (!code) return null;
  if (code.startsWith('rev')) return 'revenue';
  if (code === 'cost.salary') return 'cost.salary';
  if (code.startsWith('cost.var')) return 'cost.variable';
  if (code.startsWith('cost.fixed') && code !== 'cost.fixed.total') return 'cost.fixed';
  return null;
}

/** A code is valid iff it categorises into a family. */
export function isValidLineCode(code: string): boolean {
  return lineCodeFamily(code) !== null;
}

export const FAMILY_LABEL: Record<LineCodeFamily, string> = {
  revenue: 'Revenue',
  'cost.salary': 'Salary',
  'cost.variable': 'Variable',
  'cost.fixed': 'Fixed',
};

export const FAMILY_BADGE: Record<LineCodeFamily, 'green' | 'amber' | 'blue' | 'gray'> = {
  revenue: 'green',
  'cost.salary': 'amber',
  'cost.variable': 'blue',
  'cost.fixed': 'gray',
};
